import {
  Task,
  TaskMemberRole,
  TaskPriority,
  TaskStatus,
} from '@graphql/generated';

// In-memory equivalents of the backend listTasks filters and due-group
// scopes (tasktag-backend app/graphql/queries/task/list_tasks_query.rb and
// Task.today_tasks / Task.upcoming_tasks). They let ProjectTasks.web derive
// the Current/Overdue/Completed sections and the filtered views from a
// single cached project task list instead of issuing one network-only
// query per section/filter combination (TA-10112).

export type ProjectTaskFilter = {
  priorities?: TaskPriority[];
  // ISO8601 timestamps; tasks with no due date are excluded when set,
  // matching the backend `where(due_date: begin..end)` range filter.
  dueDateBegin?: string;
  dueDateEnd?: string;
  // Matches backend Task.get_users_tasks: only the Assignee role counts.
  assigneeIds?: string[];
};

// Due/start dates are stored at midnight UTC (see beginningOfDayUTC), so the
// UTC calendar date is the canonical "day" the backend compares against.
const utcDateKey = (iso: string): string | null => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

// The backend groups against "today" in the user's time zone
// (list_tasks_query.rb#user_today); the device's local date is that same day.
export const localTodayKey = (now: Date = new Date()): string => {
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isTaskAssignedTo = (task: Task, userId: string): boolean =>
  task.members?.some(
    (m) => m.user?.id === userId && m.role === TaskMemberRole.Assignee
  ) ?? false;

export const filterTasks = (
  tasks: Task[],
  { priorities, dueDateBegin, dueDateEnd, assigneeIds }: ProjectTaskFilter
): Task[] => {
  const begin = dueDateBegin ? new Date(dueDateBegin).getTime() : undefined;
  const end = dueDateEnd ? new Date(dueDateEnd).getTime() : undefined;

  return tasks.filter((task) => {
    if (
      priorities?.length &&
      (!task.priority || !priorities.includes(task.priority))
    ) {
      return false;
    }

    if (begin !== undefined || end !== undefined) {
      if (!task.dueDate) return false;
      const due = new Date(task.dueDate).getTime();
      if (Number.isNaN(due)) return false;
      if (begin !== undefined && due < begin) return false;
      if (end !== undefined && due > end) return false;
    }

    if (
      assigneeIds?.length &&
      !assigneeIds.some((id) => isTaskAssignedTo(task, id))
    ) {
      return false;
    }

    return true;
  });
};

export type TaskDueBucket = 'CURRENT' | 'OVERDUE' | null;

// Mirrors Task.today_tasks / Task.upcoming_tasks (their union is the
// backend's CURRENT group) and the OVERDUE scope `due_date < today`.
export const getTaskDueBucket = (
  task: Pick<Task, 'startDate' | 'dueDate'>,
  todayKey: string
): TaskDueBucket => {
  const due = task.dueDate ? utcDateKey(task.dueDate) : null;
  const start = task.startDate ? utcDateKey(task.startDate) : null;

  if (due && due < todayKey) return 'OVERDUE';

  const isToday =
    (!!start && !!due && start <= todayKey && due >= todayKey) ||
    (!start && due === todayKey) ||
    (!!start && !due && start <= todayKey);

  const isUpcoming =
    (!!start && !!due && start > todayKey && due > todayKey) ||
    (!start && !due) ||
    (!start && !!due && due > todayKey) ||
    (!!start && !due && start > todayKey);

  return isToday || isUpcoming ? 'CURRENT' : null;
};

export type GroupedProjectTasks = {
  currentTasks: Task[];
  overdueTasks: Task[];
  completedTasks: Task[];
};

// Input order is preserved within each group, so server-side sorting on the
// source query carries through to the derived sections.
export const groupTasksByDueGroup = (
  tasks: Task[],
  todayKey: string
): GroupedProjectTasks => {
  const currentTasks: Task[] = [];
  const overdueTasks: Task[] = [];
  const completedTasks: Task[] = [];

  for (const task of tasks) {
    // A task is Completed when either signal is set; groups are exclusive so
    // a task can never render in two sections at once.
    if (task.status === TaskStatus.Completed) {
      completedTasks.push(task);
      continue;
    }
    // Backend excludeCompleted checks completed_at, independently of status.
    if (task.completedAt) continue;

    const bucket = getTaskDueBucket(task, todayKey);
    if (bucket === 'CURRENT') currentTasks.push(task);
    else if (bucket === 'OVERDUE') overdueTasks.push(task);
  }

  return { currentTasks, overdueTasks, completedTasks };
};
